import { useEffect, useRef, useState } from "react"
import { useActiveSpeakerId, useParticipantIds } from "./callTransport"
import { ACTIVE_SPEAKER_MIN_HOLD_MS, ACTIVE_SPEAKER_PROMOTE_MS } from "./constants"

/**
 * Stabilised active speaker for the stage.
 *
 * Daily's `active-speaker-change` fires on every brief sound. A candidate must remain the
 * active speaker for ACTIVE_SPEAKER_PROMOTE_MS before it takes the stage, and once promoted
 * it holds the stage for at least ACTIVE_SPEAKER_MIN_HOLD_MS. Silence never clears the stage;
 * only a participant leaving does.
 */
export function useActiveSpeakerHysteresis({ ignoreLocal = true }: { ignoreLocal?: boolean } = {}): string | null {
  const candidate = useActiveSpeakerId({ ignoreLocal })
  const participantIds = useParticipantIds()
  const [committed, setCommitted] = useState<string | null>(null)
  const committedAtRef = useRef(0)

  useEffect(() => {
    if (committed && !participantIds.includes(committed)) {
      setCommitted(null)
      committedAtRef.current = 0
    }
  }, [committed, participantIds])

  useEffect(() => {
    if (!candidate || candidate === committed) return
    const now = Date.now()
    const holdRemaining = committedAtRef.current ? committedAtRef.current + ACTIVE_SPEAKER_MIN_HOLD_MS - now : 0
    const delay = Math.max(ACTIVE_SPEAKER_PROMOTE_MS, holdRemaining)
    const timer = window.setTimeout(() => {
      setCommitted(candidate)
      committedAtRef.current = Date.now()
    }, delay)
    return () => window.clearTimeout(timer)
  }, [candidate, committed])

  return committed
}
